// src/constants/index.ts
var SUBSCRIPTION_TIERS = {
  FREE: "free",
  DRIVER: "driver",
  BUSINESS: "business",
  BUSINESS_PLUS: "business_plus",
  BUSINESS_ULTIMATE: "business_ultimate"
};
var SUBSCRIPTION_STATUS = {
  ACTIVE: "active",
  TRIALING: "trialing",
  PAST_DUE: "past_due",
  CANCELLED: "cancelled",
  INACTIVE: "inactive"
};
var COMPANY_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended"
};
var COMPANY_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  DRIVER: "driver"
};
var PLATFORM_ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  SUPPORT: "support",
  VERIFIER: "verifier",
  DEVELOPER: "developer",
  READ_ONLY: "read_only"
};
var PLATFORM_PERMISSIONS = {
  VIEW_ALL_DATA: "view_all_data",
  EDIT_USERS: "edit_users",
  EDIT_COMPANIES: "edit_companies",
  VERIFY_COMPANIES: "verify_companies",
  EDIT_RESERVATIONS: "edit_reservations",
  MANAGE_SUBSCRIPTIONS: "manage_subscriptions",
  MANAGE_PLATFORM_ADMINS: "manage_platform_admins",
  VIEW_ANALYTICS: "view_analytics",
  ACCESS_LOGS: "access_logs"
};
var RESERVATION_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};
var RESERVATION_STATUS_TRANSITIONS = {
  pending: ["confirmed", "cancelled"],
  confirmed: ["assigned", "cancelled"],
  assigned: ["in_progress", "confirmed", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: []
};
function canTransitionReservation(from, to) {
  return RESERVATION_STATUS_TRANSITIONS[from].includes(to);
}
function getReservationStage(input) {
  if (input.status === RESERVATION_STATUS.ASSIGNED) {
    return input.driverAcceptedAt ? "driver_confirmed" : "awaiting_driver";
  }
  return input.status;
}
var RESERVATION_STAGE_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  awaiting_driver: "Awaiting driver",
  driver_confirmed: "Driver confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled"
};
var PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PAID: "paid",
  PARTIAL: "partial"
};
var PAYMENT_METHODS = {
  CASH: "cash",
  VENMO: "venmo",
  ZELLE: "zelle",
  PAYPAL: "paypal",
  CHECK: "check",
  BANK_TRANSFER: "bank_transfer",
  CREDIT_CARD: "credit_card",
  OTHER: "other",
  /**
   * Online card payment through Stripe Connect (docs/stripe-connect-spec.md).
   * Written ONLY by the `payment_intent.succeeded` webhook handler — it is
   * deliberately absent from `paymentMethodSchema` so staff can never record
   * a manual payment as "stripe" (the webhook is the single writer).
   */
  STRIPE: "stripe"
};
var RIDE_TYPES = {
  POINT_TO_POINT: "point-to-point",
  HOURLY: "hourly",
  CUSTOM: "custom"
};
var RIDE_SUB_TYPES = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure"
};
var DOCUMENT_TYPES = {
  DRIVER_LICENSE: "driver_license",
  INSURANCE: "insurance",
  VEHICLE_REGISTRATION: "vehicle_registration",
  OTHER: "other"
};
var DOCUMENT_ACCESS_STATUS = {
  PENDING: "pending",
  GRANTED: "granted",
  DENIED: "denied",
  REVOKED: "revoked"
};
var DOCUMENT_REVIEW_STATUS = {
  APPROVED: "approved",
  REJECTED: "rejected"
};
var DRIVER_APPROVAL_STATUS = {
  PENDING_DOCUMENTS: "pending_documents",
  DOCUMENTS_RECEIVED: "documents_received",
  APPROVED: "approved",
  REJECTED: "rejected",
  MORE_INFO_NEEDED: "more_info_needed"
};
var PLATFORM_APPROVAL_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected"
};
var DRIVER_REJECTION_REASONS = {
  INCOMPLETE_APPLICATION: "incomplete_application",
  SUSPICIOUS_ACTIVITY: "suspicious_activity",
  DUPLICATE_APPLICATION: "duplicate_application",
  SERVICE_AREA_UNAVAILABLE: "service_area_unavailable",
  OTHER: "other"
};
var DRIVER_REJECTION_REASON_LABELS = {
  [DRIVER_REJECTION_REASONS.INCOMPLETE_APPLICATION]: "Incomplete application information",
  [DRIVER_REJECTION_REASONS.SUSPICIOUS_ACTIVITY]: "Suspicious account activity detected",
  [DRIVER_REJECTION_REASONS.DUPLICATE_APPLICATION]: "Duplicate application detected",
  [DRIVER_REJECTION_REASONS.SERVICE_AREA_UNAVAILABLE]: "Service not available in your area",
  [DRIVER_REJECTION_REASONS.OTHER]: "Other (see notes)"
};
var REGISTRATION_TYPES = {
  CUSTOMER: "customer",
  // No approval needed
  DRIVER: "driver",
  // Platform approval required
  COMPANY_ADMIN: "company_admin"
  // Platform approval required
};
var FLEET_SIZES = {
  ONE: "1",
  TWO_TO_FIVE: "2-5",
  SIX_TO_TEN: "6-10",
  ELEVEN_TO_TWENTYFIVE: "11-25",
  TWENTYSIX_PLUS: "26+"
};
var INVITATION_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  EXPIRED: "expired",
  CANCELLED: "cancelled"
};
var FLIGHT_STATUS = {
  SCHEDULED: "scheduled",
  DELAYED: "delayed",
  DEPARTED: "departed",
  ARRIVED: "arrived",
  CANCELLED: "cancelled"
};
var AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
  BUSY: "busy"
};
var COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "AF", name: "Afghanistan" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brazil" },
  { code: "BN", name: "Brunei" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cabo Verde" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo (Democratic Republic)" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "C\xF4te d'Ivoire" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GR", name: "Greece" },
  { code: "GD", name: "Grenada" },
  { code: "GT", name: "Guatemala" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HN", name: "Honduras" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KP", name: "Korea (North)" },
  { code: "KR", name: "Korea (South)" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "MK", name: "North Macedonia" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "QA", name: "Qatar" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VA", name: "Vatican City" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" }
];
var ERROR_CODES = {
  // Authentication
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  ACCOUNT_TYPE_CONFLICT: "ACCOUNT_TYPE_CONFLICT",
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  // Authorization
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  COMPANY_CONTEXT_MISSING: "COMPANY_CONTEXT_MISSING",
  ROLE_REQUIRED: "ROLE_REQUIRED",
  // Resources
  RESOURCE_NOT_FOUND: "RESOURCE_NOT_FOUND",
  RESOURCE_ALREADY_EXISTS: "RESOURCE_ALREADY_EXISTS",
  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_STATE_TRANSITION: "INVALID_STATE_TRANSITION",
  // Subscription
  FEATURE_NOT_AVAILABLE: "FEATURE_NOT_AVAILABLE",
  LIMIT_EXCEEDED: "LIMIT_EXCEEDED",
  SUBSCRIPTION_REQUIRED: "SUBSCRIPTION_REQUIRED",
  // Rate Limiting
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  // Server
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE"
};
var HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
};
var INQUIRY_STATUS = {
  NEW: "new",
  CONTACTED: "contacted",
  CONVERTED: "converted",
  CLOSED: "closed"
};
var INQUIRY_PLAN_OPTIONS = {
  BUSINESS: "business",
  BUSINESS_PLUS: "business_plus",
  BUSINESS_ULTIMATE: "business_ultimate",
  HELP_ME_CHOOSE: "help_me_choose"
};
var COMPANY_PLAN_DETAILS = {
  business: {
    id: "business",
    name: "Business",
    price: 99,
    maxDrivers: 10,
    reservationsPerMonth: 1e3,
    features: [
      "Up to 10 drivers",
      "1,000 reservations/month",
      "Driver management & assignment",
      "Basic analytics",
      "Custom branding",
      "SMS notifications (200/month included)",
      "Calendar sync",
      "Email support"
    ]
  },
  business_plus: {
    id: "business_plus",
    name: "Business Plus",
    price: 199,
    maxDrivers: 25,
    reservationsPerMonth: null,
    // unlimited
    features: [
      "Up to 25 drivers",
      "Unlimited reservations",
      "Advanced analytics",
      "Embeddable booking widget",
      "Promo codes & loyalty program",
      "Recurring reservations",
      "API access",
      "SMS notifications (500/month included)",
      "Priority support"
    ]
  },
  business_ultimate: {
    id: "business_ultimate",
    name: "Business Ultimate",
    price: 499,
    maxDrivers: null,
    // unlimited
    reservationsPerMonth: null,
    // unlimited
    features: [
      "Unlimited drivers",
      "Unlimited reservations",
      "Flight tracking included",
      "AI auto-dispatch",
      "Audit logs",
      "Multi-location support",
      "Custom subdomain",
      "SMS notifications (2,000/month included)",
      "Dedicated account manager & 24/7 support"
    ]
  }
};
var DRIVER_PLAN_DETAILS = {
  free_driver: {
    id: "free_driver",
    name: "Free",
    price: 0,
    reservationsPerMonth: 10,
    features: [
      "Up to 10 reservations/month",
      "Basic booking page",
      "Email notifications",
      "Mobile app access"
    ]
  },
  paid_driver: {
    id: "paid_driver",
    name: "Professional",
    price: 29,
    reservationsPerMonth: 200,
    // NOTE: no "SMS notifications" bullet — the paid_driver tier does NOT
    // include notification SMS (allowSmsNotifications=false; SMS starts at
    // Business). Drift-tested in apps/api/tests/config/planConstantsDrift.test.ts.
    features: [
      "Up to 200 reservations/month",
      "Custom booking page",
      "Customer management",
      "Online card payments (Stripe)",
      "Payment tracking",
      "Priority support"
    ]
  }
};

export { AVAILABILITY_STATUS, COMPANY_PLAN_DETAILS, COMPANY_ROLES, COMPANY_STATUS, COUNTRIES, DOCUMENT_ACCESS_STATUS, DOCUMENT_REVIEW_STATUS, DOCUMENT_TYPES, DRIVER_APPROVAL_STATUS, DRIVER_PLAN_DETAILS, DRIVER_REJECTION_REASONS, DRIVER_REJECTION_REASON_LABELS, ERROR_CODES, FLEET_SIZES, FLIGHT_STATUS, HTTP_STATUS, INQUIRY_PLAN_OPTIONS, INQUIRY_STATUS, INVITATION_STATUS, PAYMENT_METHODS, PAYMENT_STATUS, PLATFORM_ADMIN_ROLES, PLATFORM_APPROVAL_STATUS, PLATFORM_PERMISSIONS, REGISTRATION_TYPES, RESERVATION_STAGE_LABELS, RESERVATION_STATUS, RESERVATION_STATUS_TRANSITIONS, RIDE_SUB_TYPES, RIDE_TYPES, SUBSCRIPTION_STATUS, SUBSCRIPTION_TIERS, canTransitionReservation, getReservationStage };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map